// QuizResult.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./QuizResult.css";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import SkillTreePreviewComponent from "./SkillsTreePreviewComponent";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DownloadIcon from "@mui/icons-material/Download";
import { getQuizResultByIdAction } from "../../../actions/Badge/badgeActions";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { generateTags } from "../../../utils/constants";
import CheckIcon from "@mui/icons-material/Check";
import BadgeLeaderboard from "./BadgeLeaderboard";
import Confetti from "react-confetti";
import { createPortal } from "react-dom";
import useWindowSize from "react-use/lib/useWindowSize";
import { getLeaderboardDataAction } from "../../../actions/Leaderboard/leaderboardActions";
import { APIRequestURI } from "../../../utils/api_request_uri";
import EducationTopBar from "../topbar/EducationTopBar";
import useWindowResize from "../../../utils/WindowResize";

const LinkedInSharePopup = ({ open, onClose, result }) => {
  const [copiedIcon, setCopiedIcon] = useState(false);
  const fieldKey = result?.data?.quiz?.technologyFieldId?.sub_id;
  const tags = generateTags(fieldKey);

  const shareText = `🎉 I'm excited to share that I've earned my ${result?.data?.quiz?.technologyFieldId?.name} - ${result?.data?.quiz?.technologySubfieldId?.name} Badge! 🚀

This achievement represents my dedication to mastering ${result?.data?.quiz?.technologySubfieldId?.name} and building modern web applications.

${tags}`;

  const handleLinkedInShare = () => {
    navigator.clipboard.writeText(shareText);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      window.location.href,
    )}`;
    window.open(linkedInUrl, "_blank", "width=600,height=600");
    onClose();
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(shareText);
    setCopiedIcon(true);
    setTimeout(() => {
      setCopiedIcon(false);
    }, 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1a1a1a",
          border: "1px solid #333333",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          fontFamily: '"Questrial", sans-serif',
          // width: "350px",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#ffffff",
          fontSize: "22px",
          fontWeight: 700,
          fontFamily: '"Questrial", sans-serif',
          borderBottom: "1px solid #333333",
          pb: 2,
        }}
      >
        Share your Achievement on LinkedIn
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography
          sx={{
            mb: 2,
            color: "#b0b0b0",
            fontSize: "15px",
            fontFamily: '"Questrial", sans-serif',
          }}
        >
          Copy the text below and paste it in your LinkedIn post:
        </Typography>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <TextField
            multiline
            fullWidth
            minRows={6}
            value={shareText}
            variant="outlined"
            InputProps={{
              readOnly: true,
              sx: {
                color: "#ffffff",
                fontFamily: '"Questrial", sans-serif',
                fontSize: "14px",
                backgroundColor: "#0a0a0a",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#333333",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#555555",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ffd700",
                  borderWidth: "2px",
                },
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "12px",
              },
            }}
          />
          <IconButton
            onClick={handleCopyText}
            size="large"
            sx={{
              mt: 0.5,
              color: "#ffd700",
              backgroundColor: "#0a0a0a",
              border: "1px solid #333333",
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "#1a1a1a",
                borderColor: "#ffd700",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {copiedIcon ? <CheckIcon /> : <ContentCopyIcon />}
          </IconButton>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #333333",
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: "#b0b0b0",
            fontFamily: '"Questrial", sans-serif',
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "inherit",
            padding: "10px 24px",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#0a0a0a",
              color: "#ffffff",
            },
            transition: "all 0.3s ease",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleLinkedInShare}
          sx={{
            backgroundColor: "#0a66c2",
            color: "#ffffff",
            fontFamily: '"Questrial", sans-serif',
            fontSize: "14px",
            fontWeight: 700,
            textTransform: "inherit",
            padding: "10px 28px",
            borderRadius: "12px",
            transition: "all 0.3s ease",
          }}
        >
          Open LinkedIn
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PerformanceDialog = ({ open, onClose, result }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1a1a1a",
          border: "1px solid #333333",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          fontFamily: '"Questrial", sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#ffffff",
          fontSize: "20px",
          fontWeight: 700,
          fontFamily: '"Questrial", sans-serif',
          borderBottom: "1px solid #333333",
          pb: 2,
          position: "relative",
          pr: 6,
        }}
      >
        Your Performance
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#b0b0b0",
            "&:hover": {
              color: "#ffffff",
              backgroundColor: "#0a0a0a",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        <div className="quizresult-stats">
          <div className="quizresult-stat-item">
            <span className="quizresult-stat-value">
              {result?.data?.scorePercent}%
            </span>
            <span className="quizresult-stat-label">Score</span>
          </div>
          <div className="quizresult-stat-item">
            <span className="quizresult-stat-value">Passed</span>
            <span className="quizresult-stat-label">Status</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuizResult = ({
  userName = "John Doe",
  badgeImageUrl = "/badge.png",
}) => {
  const urlData = useParams();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { result, success, loading } = useSelector(
    (state) => state.getQuizResultStore,
  );
  const history = useHistory();
  const isSmallScreen = useWindowResize();
  const { width, height } = useWindowSize();
  const dispatch = useDispatch();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [openPerformanceDialog, setOpenPerformanceDialog] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Breathing animation configuration
  const breathingAnimation = {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  // Handle badge download
  const handleDownload = async () => {
    const badgeData = result?.data?.badgeAwarded;

    const imageUrl = badgeData?.imageUrl;
    const title = badgeData?.title;
    const BASE_URL = new APIRequestURI().getBaseURI();

    const downloadUrl =
      `${BASE_URL}/api/badges/download` +
      `?url=${encodeURIComponent(imageUrl)}` +
      `&title=${encodeURIComponent(title)}`;

    window.location.href = downloadUrl;
  };

  // Handle LinkedIn share
  const handleLinkedInShare = () => {
    setOpenPopup(true);
  };

  // Handle view results
  const handleViewResults = () => {
    setOpenPerformanceDialog(true);
  };

  useEffect(() => {
    if (userInfo) {
      dispatch(getQuizResultByIdAction(urlData?.id));
    }
  }, [userInfo, dispatch, urlData]);

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  const BADGE_BASE_URL =
    "https://pub-1faf68ce26a54cbf8203a339879a01d6.r2.dev/badges";

  const badgeImageMap = {
    frontend: "generated-image.png",
    backend: "backend-development.png",
    fullstack: "fullstack-development.png",
    mobile: "mobile-app-development.png",
    ai: "ai-ml-development.png",
  };

  const getBadgeImageUrl = (subId) => {
    const fileName = badgeImageMap[subId];
    return fileName
      ? `${BADGE_BASE_URL}/${fileName}`
      : `${BADGE_BASE_URL}/generated-image.png`;
  };

  return (
    <div>
      <EducationTopBar
        logo={
          <div
            onClick={() => history.push("/")}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <img
              className={isSmallScreen ? "mobile-logo" : "desktop-logo"}
              src="https://res.cloudinary.com/cqn/image/upload/v1740519381/white_acetechies_logo_vsdhp5.png"
              alt=""
            />
          </div>
        }
      />

      <div className="quizresult-container">
        {/* Full Page Lock Overlay for non-logged-in users */}
        {!userInfo && (
          <motion.div
            className="quizresult-fullpage-lock"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="quizresult-lock-content"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <svg
                className="quizresult-lock-icon"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"
                  fill="currentColor"
                />
              </svg>
              <h2 className="quizresult-lock-title">
                Login to View Your Results
              </h2>
              <p className="quizresult-lock-subtitle">
                Sign in to download your badge, view your performance details,
                and see where you rank on the leaderboard
              </p>
              <motion.button
                className="quizresult-login-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Login Now
              </motion.button>
            </motion.div>
          </motion.div>
        )}

        {/* Main Content */}
        <div
          className={`quizresult-main-content ${!userInfo ? "blurred" : ""}`}
        >
          {/* Congratulations Section */}
          <motion.div
            className="quizresult-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="quizresult-title">
              Congratulations🎉 on Earning your{" "}
              <span className="quizresult-title-main-heading">
                {result?.data?.quiz?.technologyFieldId?.name} -{" "}
                {result?.data?.quiz?.technologySubfieldId?.name}{" "}
              </span>{" "}
              Badge
            </h1>
          </motion.div>

          {/* Desktop Layout - Two Column */}
          <div className="quizresult-desktop-layout">
            {/* Left Column - Badge */}
            <motion.div
              className="quizresult-left-column"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <motion.div
                className="quizresult-badge-wrapper"
                animate={breathingAnimation}
              >
                <img
                  src={result?.data?.badgeAwarded?.imageUrl}
                  alt={result?.data?.badgeAwarded?.title}
                  className="quizresult-badge-image"
                  onLoad={() => setImageLoaded(true)}
                />
              </motion.div>

              {/* Button Group */}
              <div className="quizresult-button-group">
                <Button
                  className="quizresult-download-btn-gold"
                  onClick={handleDownload}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <DownloadIcon />
                  Download
                </Button>

                {isMobile && (
                  <Button
                    className="quizresult-view-results-btn"
                    onClick={handleViewResults}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 }}
                  >
                    <AssignmentIcon sx={{ marginRight: "10px" }} />
                    View Results
                  </Button>
                )}
              </div>

              {/* Performance Details - Desktop Only */}
              {!isMobile && (
                <motion.div
                  className="quizresult-performance"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                >
                  <h2 className="quizresult-section-title">Your Performance</h2>
                  <div className="quizresult-stats">
                    <div className="quizresult-stat-item">
                      <span className="quizresult-stat-value">
                        {result?.data?.scorePercent}%
                      </span>
                      <span className="quizresult-stat-label">Score</span>
                    </div>
                    <div className="quizresult-stat-item">
                      <span className="quizresult-stat-value">Passed</span>
                      <span className="quizresult-stat-label">Status</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Right Column - Details */}
            <div className="quizresult-right-column">
              {/* LinkedIn Share Section */}
              <motion.div
                className="quizresult-share-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
              >
                <h3 style={{ margin: "5px 0px" }}>
                  Inspire others! Share your{" "}
                  <span className="quizresult-title-main-heading">
                    {result?.data?.quiz?.technologySubfieldId?.name}{" "}
                  </span>
                  Badge on LinkedIn
                </h3>
                <span>
                  Show the world your achievement and help your peers discover
                  our learning journey
                </span>
                <motion.button
                  className="quizresult-linkedin-btn"
                  onClick={handleLinkedInShare}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    className="quizresult-linkedin-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.667 2.5H3.333C2.875 2.5 2.5 2.875 2.5 3.333V16.667C2.5 17.125 2.875 17.5 3.333 17.5H16.667C17.125 17.5 17.5 17.125 17.5 16.667V3.333C17.5 2.875 17.125 2.5 16.667 2.5ZM7.083 15H4.583V8.333H7.083V15ZM5.833 7.25C5.042 7.25 4.417 6.625 4.417 5.833C4.417 5.042 5.042 4.417 5.833 4.417C6.625 4.417 7.25 5.042 7.25 5.833C7.25 6.625 6.625 7.25 5.833 7.25ZM15.417 15H12.917V11.75C12.917 10.833 12.9 9.667 11.583 9.667C10.25 9.667 10.083 10.667 10.083 11.667V15H7.583V8.333H10V9.333H10.042C10.417 8.667 11.292 8 12.583 8C15.125 8 15.417 9.667 15.417 11.667V15Z"
                      fill="currentColor"
                    />
                  </svg>
                  Share on LinkedIn
                </motion.button>
                <p className="quizresult-share-note">
                  ✨ 427 learners shared their badge last week!
                </p>
              </motion.div>

              <motion.div>
                <SkillTreePreviewComponent
                  techFieldName={result?.data?.quiz?.technologyFieldId?.name}
                  sub_id={result?.data?.quiz?.technologyFieldId?.sub_id}
                />
              </motion.div>

              {/* Leaderboard Section */}
              <motion.div
                className="quizresult-leaderboard-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4 }}
              >
                <BadgeLeaderboard
                  result={result}
                  certificateImage={getBadgeImageUrl(
                    result?.data?.quiz?.technologyFieldId?.name,
                  )}
                  certificateName={`${result?.data?.quiz?.technologyFieldId?.name} - ${result?.data?.quiz?.technologySubfieldId?.name}`}
                  technologyFieldName={
                    result?.data?.quiz?.technologyFieldId?.name
                  }
                  sub_id={result?.data?.quiz?.technologyFieldId?.sub_id}
                />
              </motion.div>
            </div>
          </div>
        </div>

        {showConfetti &&
          createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 1400,
                pointerEvents: "none",
              }}
            >
              <Confetti
                width={width}
                height={height}
                recycle={false}
                numberOfPieces={700}
                gravity={0.3}
                initialVelocityY={25}
                confettiSource={{
                  x: width / 100, // center horizontally
                  y: 0,
                  w: width,
                  h: 10,
                }}
                onConfettiComplete={handleConfettiComplete}
              />
            </div>,
            document.body,
          )}

        <LinkedInSharePopup
          onClose={() => setOpenPopup(false)}
          open={openPopup}
          result={result}
        />

        <PerformanceDialog
          open={openPerformanceDialog}
          onClose={() => setOpenPerformanceDialog(false)}
          result={result}
        />
      </div>
    </div>
  );
};

export default QuizResult;
